import type { Measure } from '@/types';

const DEFAULT_CURSOR_POSITION = 0.25;

/**
 * A (time, x) anchor point for cursor interpolation, finer-grained than a
 * measure boundary. Sourced from alphaTab's own `BeatBounds.onNotesX` --
 * "the x-position where the timely center of the notes for this beat is...
 * where the cursor should be at the time when this beat is played" -- i.e.
 * alphaTab's own intended per-beat cursor target, not a derived value.
 */
export interface ScrollAnchor {
  time: number;
  x: number;
}

export interface ComputeScrollLeftParams {
  currentTime: number;
  measures: Measure[];
  totalDuration: number;
  measureXMap: Map<number, number>;
  contentScrollWidth: number;
  containerWidth: number;
  cursorPosition?: number;
  /**
   * Optional per-beat anchors (see `ScrollAnchor`). When anchors fall within
   * the current measure's time window, the cursor interpolates between them
   * instead of assuming notes are spaced uniformly-in-time across the whole
   * measure -- the latter drifts within any measure containing mixed note
   * durations (real engraving spaces notes by duration class/density, not
   * linearly by time), which is the common case, not an edge case.
   */
  beatAnchors?: ScrollAnchor[];
}

/** Interpolates x for `time` across a sorted (ascending by time) anchor
 *  list with at least 2 entries -- caller guarantees this by always
 *  bracketing with the measure-boundary anchors. */
function interpolateAnchors(anchors: ScrollAnchor[], time: number): number {
  let i = 0;
  while (i < anchors.length - 2 && time >= anchors[i + 1].time) i++;
  const a = anchors[i];
  const b = anchors[i + 1];
  const span = b.time - a.time;
  const t = span > 0 ? Math.max(0, Math.min(1, (time - a.time) / span)) : 0;
  return a.x + t * (b.x - a.x);
}

export function computeScrollLeft({
  currentTime,
  measures,
  totalDuration,
  measureXMap,
  contentScrollWidth,
  containerWidth,
  cursorPosition = DEFAULT_CURSOR_POSITION,
  beatAnchors,
}: ComputeScrollLeftParams): number {
  const clampedTime = Math.max(0, Math.min(currentTime, totalDuration));

  let contentX: number;

  if (measureXMap.size === 0 || measures.length === 0) {
    const ratio = totalDuration > 0 ? clampedTime / totalDuration : 0;
    contentX = ratio * contentScrollWidth;
  } else {
    let currentMeasure = measures[0];
    for (let i = measures.length - 1; i >= 0; i--) {
      if (clampedTime >= measures[i].startTime) {
        currentMeasure = measures[i];
        break;
      }
    }

    const baseX = measureXMap.get(currentMeasure.number) ?? 0;
    const nextX = measureXMap.get(currentMeasure.number + 1) ?? contentScrollWidth;
    const nextMeasureStart =
      measures.find((m) => m.number === currentMeasure.number + 1)?.startTime ?? totalDuration;

    const inMeasureAnchors = beatAnchors?.filter(
      (a) => a.time >= currentMeasure.startTime && a.time <= nextMeasureStart
    );

    if (inMeasureAnchors && inMeasureAnchors.length > 0) {
      const bracketed: ScrollAnchor[] = [
        { time: currentMeasure.startTime, x: baseX },
        ...inMeasureAnchors,
        { time: nextMeasureStart, x: nextX },
      ];
      contentX = interpolateAnchors(bracketed, clampedTime);
    } else {
      const t =
        currentMeasure.duration > 0
          ? Math.max(
              0,
              Math.min(1, (clampedTime - currentMeasure.startTime) / currentMeasure.duration)
            )
          : 0;
      contentX = baseX + t * (nextX - baseX);
    }
  }

  return Math.max(0, contentX - containerWidth * cursorPosition);
}

export function buildMeasureXMap(contentEl: HTMLDivElement, zoom: number): Map<number, number> {
  const map = new Map<number, number>();
  const containerRect = contentEl.getBoundingClientRect();
  const texts = contentEl.querySelectorAll<SVGTextElement>('svg text');

  texts.forEach((el) => {
    const txt = el.textContent?.trim() ?? '';
    const n = parseInt(txt, 10);
    if (isNaN(n) || String(n) !== txt || n < 1) return;

    const rect = el.getBoundingClientRect();
    const unscaledX = (rect.left - containerRect.left) / zoom;

    const existing = map.get(n);
    if (existing === undefined || unscaledX < existing) {
      map.set(n, unscaledX);
    }
  });

  if (map.size === 0) {
    console.warn(
      '[NotationCursor] No bar numbers found in alphaTab SVG — using proportional fallback.'
    );
  }

  return map;
}
